import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { finalize } from 'rxjs';
import { CustomerSummary } from '../models/auth.models';
import { AuthService } from '../services/auth.service';
import { MicrosoftSsoService } from '../services/microsoft-sso.service';

@Component({
  selector: 'app-customer-selection',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './customer-selection.component.html',
  styleUrl: './customer-selection.component.css'
})
export class CustomerSelectionComponent implements OnInit {
  customers: CustomerSummary[] = [];
  loading = true;
  selectingCustomerId: string | null = null;
  error: string | null = null;

  constructor(
    private readonly authService: AuthService,
    private readonly microsoftSsoService: MicrosoftSsoService,
    private readonly router: Router
  ) {}

  get user() {
    return this.authService.getSession()?.user ?? null;
  }

  ngOnInit(): void {
    this.loadCustomers();
  }

  loadCustomers(): void {
    this.loading = true;
    this.error = null;

    this.authService.getAvailableCustomers()
      .pipe(finalize(() => {
        this.loading = false;
      }))
      .subscribe({
        next: response => {
          this.customers = [...response.customers].sort((left, right) =>
            left.name.localeCompare(right.name)
          );
        },
        error: error => {
          this.error = this.getErrorMessage(
            error,
            'Customers could not be loaded. Please try again.'
          );
        }
      });
  }

  selectCustomer(customer: CustomerSummary): void {
    if (this.selectingCustomerId) return;

    this.selectingCustomerId = customer.id;
    this.error = null;

    this.authService.selectCustomer(customer.id)
      .pipe(finalize(() => {
        this.selectingCustomerId = null;
      }))
      .subscribe({
        next: () => {
          void this.router.navigate(['/']);
        },
        error: error => {
          const fallback = error.status === 503
            ? `${customer.name} is temporarily unavailable. Please choose another customer.`
            : 'The customer could not be opened. Please try again.';
          this.error = this.getErrorMessage(error, fallback);
        }
      });
  }

  async logout(): Promise<void> {
    this.authService.logout();
    try {
      await this.microsoftSsoService.logout();
    } catch {
      await this.router.navigate(['/login']);
    }
  }

  trackCustomer(_: number, customer: CustomerSummary): string {
    return customer.id;
  }

  private getErrorMessage(error: any, fallback: string): string {
    return error?.error?.detail
      || error?.error?.message
      || fallback;
  }
}
